/**
 * Recognises a failed code-split chunk fetch (FR-242).
 *
 * Every deploy rewrites the hashed asset filenames, so a tab left open across a
 * deploy holds an `index.html` whose chunk URLs no longer exist. The dynamic
 * import behind `React.lazy` then rejects and the surface shows its error
 * boundary. Distinguishing that from a genuine crash inside the component
 * matters, because the two need opposite recovery actions: a crash can be
 * retried in place, a stale chunk cannot.
 *
 * A stale chunk cannot be recovered without a reload, and not merely because
 * `React.lazy` caches the rejected promise. The browser's module map records
 * the *failed fetch* for the lifetime of the document, so re-importing the same
 * URL replays the cached failure without issuing a request at all. Measured on
 * the production preview build: three import attempts of a deleted chunk
 * produced exactly one HTTP request, with attempts 2 and 3 rejecting in under a
 * millisecond. An in-place retry is therefore incapable of recovering this —
 * only a reload, which fetches a fresh `index.html` and a new asset manifest.
 */

/**
 * Message signatures browsers use when a module script cannot be fetched or
 * parsed. Chrome, Firefox, and Safari each word this differently, and the
 * legacy webpack phrasing is kept for parity with the Sentry ignore list in
 * `lib/monitoring/sentry.ts`.
 *
 * The MIME-type phrasings matter as much as the fetch ones: `vercel.json`
 * rewrites every non-`/api` path to `index.html`, so a chunk removed by a
 * deploy returns HTML with a 200 rather than a 404, and the browser rejects it
 * for having the wrong module MIME type.
 */
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|Failed to load module script|Expected a JavaScript(-or-Wasm)? module script/i;

/** True when `error` looks like a failed module-script fetch rather than a crash inside the module. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_PATTERN.test(error.message);
}

const STALE_CHUNK_RELOAD_KEY = 'truspace.stale-chunk-reload';

/**
 * Wait until after the first lazy import has either succeeded or failed, so a
 * broken deploy cannot loop. Clearing the one-shot flag immediately at boot
 * would wipe it before `React.lazy` settles.
 */
export const STALE_CHUNK_FLAG_CLEAR_DELAY_MS = 3000;

/**
 * Reloads once after a stale-chunk error so an idle tab picks up the new
 * asset manifest. The flag lives in sessionStorage so a real missing file
 * cannot loop. Clear it on a successful boot ({@link clearStaleChunkReloadFlag})
 * so the next deploy can recover the same way.
 *
 * @returns Whether a reload was started.
 */
export function reloadOnceForStaleChunk(
  storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
  reload: () => void = () => {
    window.location.reload();
  },
): boolean {
  try {
    if (storage.getItem(STALE_CHUNK_RELOAD_KEY) === '1') return false;
    storage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
    reload();
    return true;
  } catch {
    return false;
  }
}

/** Call after the new document paints so a later deploy can auto-reload again. */
export function clearStaleChunkReloadFlag(
  storage: Pick<Storage, 'removeItem'> = sessionStorage,
): void {
  try {
    storage.removeItem(STALE_CHUNK_RELOAD_KEY);
  } catch {
    // Private-mode sessionStorage can throw; boot still proceeds.
  }
}
